import React, { useState, useEffect } from "react";
import { getSpacesByFilters } from "services/spaceList.services";
import { useLanguage } from "context/languageContext";
import { useTranslation } from "react-i18next";
import { MultiViewSlider } from "reusableComponents";
import LoadingPage from "views/LoadingPage";
import { ContactFormIcon, EmailIcon, PhoneIcon } from "./ContactsPageSvgIcons";
import styles from "./styles.module.scss";

// Import the SVG file (adjust the path as needed)
import contactOrganiserSvg from "views/HomePage/components/Banner/svgBannerMinimal.svg";
import SpaceCardStyled from "reusableComponents/SpaceCardStyled";

const ContactOrganiserPage = () => {
  const { t } = useTranslation("global");
  const { language } = useLanguage();
  const [spaces, setSpaces] = useState([]);
  const [error, setError] = useState(null);
  const [spacesLoading, setSpacesLoading] = useState(true);
  const [spacesError, setSpacesError] = useState(null);

  // Fetch spaces for slider
  useEffect(() => {
    const fetchSpaces = async () => {
      setSpacesError(null);

      try {
        const response = await getSpacesByFilters({
          order_type: "best_note",
          instant_booking: "true",
          page: "1",
          perPage: 4,
          lang: language,
        });
        setSpaces(response.data);
      } catch (err) {
        setSpacesError(err.message);
      } finally {
        setSpacesLoading(false);
      }
    };

    fetchSpaces();
  }, [language]);

  if (spacesLoading) {
    return <LoadingPage />;
  }

  return (
    <div className={`${styles.contactOrganiserContainer} pageContainer`}>
      {/* Hero Section with background */}
      <HeroSection t={t} backgroundSvg={contactOrganiserSvg} />
      
      {/* Main Content */}
      <div className={styles.contentWrapper}>
        <div className={styles.mainContent}>
          {/* Contact Methods */}
          <ContactMethodsSection t={t} />

          <ContactFormSection t={t} mobileOnly/>

          
          {/* Trust Section */}
          <TrustSection t={t} />
          
          {/* Spaces Slider */}
          <SpacesSliderSection 
            t={t} 
            spaces={spaces} 
            error={spacesError} 
          />
          
          {/* Additional Links */}
          {/* <AdditionalLinksSection t={t} /> */}
        </div>
        
        {/* Contact Form */}
        <ContactFormSection t={t} desktopOnly/>
      </div>
    </div>
  );
};

// Hero Section with background
const HeroSection = ({ t, backgroundSvg }) => (
  <div className={styles.heroSection}>
    <div className={styles.heroBackground}>
      <img 
        src={backgroundSvg} 
        alt="Contact Organiser Background" 
        className={styles.backgroundImage}
      />
    </div>
    <div className={styles.heroContent}>
      <h1 
        className={styles.heroTitle}
        dangerouslySetInnerHTML={{ __html: t("contactUs.title") }}
      />
      <p className={styles.heroText}>{t("contactUs.text")}</p>
      <h1 
        className={styles.title}
        dangerouslySetInnerHTML={{ __html: t("contactUs.secondTitle") }}
      />
    </div>
  </div>
);

const ContactMethodsSection = ({ t }) => (
  <div className={styles.contactMethodsSection}>
    <div className={styles.contactMethod}>
      <div className={styles.contactMethodHeader}>
        <EmailIcon />
        <p dangerouslySetInnerHTML={{ __html: t("contactUs.byEmail") }} />
      </div>
      <p
        className={styles.contactMethodText}
        dangerouslySetInnerHTML={{ __html: t("contactUs.byEmailText1") }}
      />
      <ContactLink 
        href="mailto:event@chooseandwork.com" 
        text="event@chooseandwork.com" 
      />
      <p
        className={styles.contactMethodText}
        dangerouslySetInnerHTML={{ __html: t("contactUs.byEmailText2") }}
      />
      <ContactLink 
        href="mailto:service.clients@chooseandwork.com" 
        text="service.clients@chooseandwork.com" 
      />
      <p
        className={styles.contactMethodText}
        dangerouslySetInnerHTML={{ __html: t("contactUs.byEmailText3") }}
      />
      <ContactLink 
        href="mailto:administratif@chooseandwork.com" 
        text="administratif@chooseandwork.com" 
      />
    </div>
    
    <div className={styles.contactMethod}>
      <div className={styles.contactMethodHeader}>
        <PhoneIcon />
        <p dangerouslySetInnerHTML={{ __html: t("contactUs.byPhone") }} />
      </div>
      <p
        className={styles.contactMethodText}
        dangerouslySetInnerHTML={{ __html: t("contactUs.byPhoneText") }}
      />
      <ContactLink 
        href="tel:+330180522522" 
        text="+33(0)1 80522522" 
      />
    </div>
  </div>
);

const ContactLink = ({ href, text }) => (
  <a className={styles.contactLink} href={href}>
    {text}
  </a>
);

const TrustSection = ({ t }) => (
  <div className={styles.trustSection}>
    <h3 
      className={styles.trustTitle}
      dangerouslySetInnerHTML={{ __html: t("contactUs.trustUsTitle") }}
    />
    <p className={styles.trustText}>{t("contactUs.trustUsText")}</p>
  </div>
);

const SpacesSliderSection = ({ t, spaces, error }) => (
  <div className={styles.spacesSection}>
    <h3
      className={styles.spacesTitle}
      dangerouslySetInnerHTML={{ __html: t("contactUs.newSpacesTitle") }}
    />
    {/* <h3
      className={styles.spacesSubtitle}
      dangerouslySetInnerHTML={{ __html: t("contactUs.newSpacesSubtitle") }}
    /> */}
    
    {error ? (
      <div className={styles.error}>Error loading spaces: {error}</div>
    ) : (
      <MultiViewSlider slidesNum={Math.min(3, spaces.length)} arrowStyle="square" arrowLeft="-44px" arrowRight="-44px">
        {spaces.map(space => (
          <div key={space.id} className={styles.spaceCardWrapper}>
            <SpaceCardStyled place={space}/>
          </div>
        ))}
      </MultiViewSlider>
    )}
  </div>
);

const AdditionalLinksSection = ({ t }) => (
  <div className={styles.linksSection}>
    <p className={styles.linksTitle}>{t("contactUs.otherLinksTitle")}</p>
    <div className={styles.links}>
      <a href="">{t("contactUs.otherLinks.link1")}</a>
      <a href="/static/cgu">{t("contactUs.otherLinks.link2")}</a>
      <a href="/faq">{t("contactUs.otherLinks.link3")}</a>
      <a href="/plan">{t("contactUs.otherLinks.link4")}</a>
      <a href="/press">{t("contactUs.otherLinks.link5")}</a>
    </div>
  </div>
);

const ContactFormSection = ({ 
  t, 
  mobileOnly = false, 
  desktopOnly = false,
  className = "" 
}) => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://webforms.pipedrive.com/f/loader";
    script.async = true;

    script.onload = () => {
      if (window.pipedriveWebForms) {
        window.pipedriveWebForms.initializeForms();
      }
    };

    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  // Determine the wrapper class based on props
  const wrapperClass = `
    ${styles.formSection} 
    ${className} 
    ${mobileOnly ? styles.mobileOnly : ""} 
    ${desktopOnly ? styles.desktopOnly : ""}
  `.trim();

  return (
    <div className={wrapperClass}>
      <div className={styles.formHeader}>
        <ContactFormIcon />
        <p dangerouslySetInnerHTML={{ __html: t("contactUs.byForm") }} />
      </div>
      <div className={styles.formContainer}>
        <p
          className={styles.formText}
          dangerouslySetInnerHTML={{ __html: t("contactUs.byFormText") }}
        />
        <div
          className="pipedriveWebForms"
          data-pd-webforms="https://webforms.pipedrive.com/f/1sTQMDiL0NRtCO0cjXlbAGVhkXbfiFWhl3QiqqeQSFLyCZY8Cf7spu3W9S8DTknxF"
        />
      </div>
    </div>
  );
};

export default ContactOrganiserPage;